/* eslint-disable @angular-eslint/prefer-inject */
import { AsnHeaderDataService } from '../infrastructure/asn-headers/asn-header.data.service';
import {
    AsnHeaderCreate,
    AsnHeaderDto,
    AsnHeaderRead,
    AsnHeaderSearchParams,
} from '../entities/asn-header.dto';
import { AsnHeaderStore } from '../infrastructure/asn-headers/asn-header.store';
import { inject, Injectable } from '@angular/core';
import { AbstractManageEntityFacade } from '@pf/shared-services';
import { AsnHeaderMapper } from '../infrastructure/asn-headers/asn-header.mapper';
import { BuildChangeTrackingFacadeFactory } from '@pf/shared/util-platform';
import {
    IManageEntityRoutes,
    IQueryParams,
    PFWmsEntities,
    SafeAny,
} from '@pf/shared-common';
import { StatusOptions } from '@pf/shared-ui';
import { AsnStatus, ResourceAuth } from '@control-tower/platform-wms';
import { map, Observable } from 'rxjs';
import { WarehouseFacade } from './warehouse.facade';

@Injectable()
export class AsnHeaderFacade extends AbstractManageEntityFacade<
    AsnHeaderDto,
    AsnHeaderRead,
    AsnHeaderCreate,
    AsnHeaderSearchParams
> {
    private readonly warehouseFacade = inject(WarehouseFacade);
    nameField: keyof AsnHeaderDto = 'externalReferenceId';

    statusOptions: StatusOptions = {
        [AsnStatus.Pending]: {
            label: 'Pending',
            status: 'primary',
        },
        [AsnStatus.Canceled]: {
            label: 'Canceled',
            color: 'danger',
        },
        [AsnStatus.InTransit]: {
            label: 'In Transit',
            status: 'secondary',
        },
        [AsnStatus.Completed]: {
            label: 'Resolved',
            status: 'info',
        },
        [AsnStatus.Delivered]: {
            label: 'Resolved',
            status: 'success',
        },
        defaultOption: {
            status: 'secondary',
            label: 'Unknown',
        },
    };

    constructor(
        dataService: AsnHeaderDataService,
        store: AsnHeaderStore,
        mapper: AsnHeaderMapper
    ) {
        super(
            dataService,
            null as unknown as IManageEntityRoutes,
            store,
            mapper
        );

        this.listenForRealTimeEvents(PFWmsEntities.AsnHeader, {
            processEventsIfNotInStore: true,
        });
    }

    override getDefaultSearchParams$(
        params: IQueryParams<AsnHeaderDto>
    ): Observable<IQueryParams<AsnHeaderDto>> {
        return this.warehouseFacade.addDefaultWarehouseParam(params);
    }

    override applyResourceAuth(body: AsnHeaderCreate) {
        const locationId = this.warehouseFacade.active?.location?.id;
        body.resourceAuth = {
            locations: locationId ? [locationId] : [],
        } as ResourceAuth;
    }

    changeTrackingFactory = BuildChangeTrackingFacadeFactory(
        this.dataService,
        this.nameField
    );

    textSearchFilter(
        searchText: string
    ): Partial<Record<keyof AsnHeaderDto, string>> {
        return { externalReferenceId: searchText };
    }

    getByPurchaseOrderId$(purchaseOrderId: string): Observable<AsnHeaderDto[]> {
        return this.searchAll$({
            pageIndex: 1,
            pageSize: 50,
            filters: {
                purchaseOrderId,
            } as SafeAny,
        });
    }

    getByTrackingNumber$(
        trackingNumber: string
    ): Observable<AsnHeaderDto | null> {
        return this.oneTimeSearch$({
            pageIndex: 1,
            pageSize: 1,
            filters: {
                trackingNumber,
            },
        }).pipe(map(result => result.data?.[0] ?? null));
    }
}
